const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const INITIAL_STATE = new Uint32Array([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
  0x1f83d9ab, 0x5be0cd19,
]);

function rotr(value: number, shift: number): number {
  return (value >>> shift) | (value << (32 - shift));
}

function compress(state: Uint32Array, block: Uint8Array): void {
  const w = new Uint32Array(64);

  for (let i = 0; i < 16; i++) {
    w[i] =
      (block[i * 4] << 24) |
      (block[i * 4 + 1] << 16) |
      (block[i * 4 + 2] << 8) |
      block[i * 4 + 3];
  }

  for (let i = 16; i < 64; i++) {
    const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
    const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
    w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
  }

  let a = state[0];
  let b = state[1];
  let c = state[2];
  let d = state[3];
  let e = state[4];
  let f = state[5];
  let g = state[6];
  let h = state[7];

  for (let i = 0; i < 64; i++) {
    const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
    const ch = (e & f) ^ (~e & g);
    const temp1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
    const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
    const maj = (a & b) ^ (a & c) ^ (b & c);
    const temp2 = (S0 + maj) >>> 0;

    h = g;
    g = f;
    f = e;
    e = (d + temp1) >>> 0;
    d = c;
    c = b;
    b = a;
    a = (temp1 + temp2) >>> 0;
  }

  state[0] = (state[0] + a) >>> 0;
  state[1] = (state[1] + b) >>> 0;
  state[2] = (state[2] + c) >>> 0;
  state[3] = (state[3] + d) >>> 0;
  state[4] = (state[4] + e) >>> 0;
  state[5] = (state[5] + f) >>> 0;
  state[6] = (state[6] + g) >>> 0;
  state[7] = (state[7] + h) >>> 0;
}

function stateToBytes(state: Uint32Array): Uint8Array {
  const out = new Uint8Array(32);
  for (let i = 0; i < 8; i++) {
    out[i * 4] = state[i] >>> 24;
    out[i * 4 + 1] = (state[i] >>> 16) & 0xff;
    out[i * 4 + 2] = (state[i] >>> 8) & 0xff;
    out[i * 4 + 3] = state[i] & 0xff;
  }
  return out;
}

export interface Sha256Hasher {
  update(data: Uint8Array): void;
  digest(): Uint8Array;
}

/** Creates an incremental SHA-256 hasher (FIPS 180-4). */
export function createSha256(): Sha256Hasher {
  const state = INITIAL_STATE.slice();
  const buffer = new Uint8Array(64);
  let bufferLength = 0;
  let totalBits = 0;
  let finished = false;

  const processBlock = (block: Uint8Array) => {
    compress(state, block);
  };

  const consume = (data: Uint8Array) => {
    let offset = 0;

    if (bufferLength > 0) {
      const take = Math.min(64 - bufferLength, data.length);
      buffer.set(data.subarray(0, take), bufferLength);
      bufferLength += take;
      offset += take;

      if (bufferLength === 64) {
        processBlock(buffer);
        bufferLength = 0;
      }
    }

    while (offset + 64 <= data.length) {
      processBlock(data.subarray(offset, offset + 64));
      offset += 64;
    }

    if (offset < data.length) {
      buffer.set(data.subarray(offset), 0);
      bufferLength = data.length - offset;
    }
  };

  return {
    update(data: Uint8Array): void {
      if (finished) {
        throw new Error('SHA-256 digest already computed');
      }
      if (data.length === 0) {
        return;
      }

      totalBits += data.length * 8;
      consume(data);
    },

    digest(): Uint8Array {
      if (finished) {
        throw new Error('SHA-256 digest already computed');
      }
      finished = true;

      buffer[bufferLength] = 0x80;
      bufferLength += 1;

      if (bufferLength > 56) {
        buffer.fill(0, bufferLength);
        processBlock(buffer);
        bufferLength = 0;
      }

      buffer.fill(0, bufferLength, 56);

      const bitLengthHi = Math.floor(totalBits / 0x100000000);
      const bitLengthLo = totalBits >>> 0;
      buffer[56] = (bitLengthHi >>> 24) & 0xff;
      buffer[57] = (bitLengthHi >>> 16) & 0xff;
      buffer[58] = (bitLengthHi >>> 8) & 0xff;
      buffer[59] = bitLengthHi & 0xff;
      buffer[60] = (bitLengthLo >>> 24) & 0xff;
      buffer[61] = (bitLengthLo >>> 16) & 0xff;
      buffer[62] = (bitLengthLo >>> 8) & 0xff;
      buffer[63] = bitLengthLo & 0xff;

      processBlock(buffer);
      return stateToBytes(state);
    },
  };
}
